import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Navbar, Nav, Container, Dropdown } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import API from '../utils/api';
import NotificationsDropdown from './NotificationsDropdown';

const Layout = () => {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [notificationCount, setNotificationCount] = useState(0);

  const isActive = (path) => location.pathname === path;

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load notification count
  useEffect(() => {
    const loadNotificationStats = async () => {
      try {
        const response = await API.get('/notifications/stats');
        setNotificationCount(response.data.unread_count);
      } catch (err) {
        console.error('Failed to load notification stats:', err);
      }
    };

    if (user) {
      loadNotificationStats();
      // Refresh notification count every 30 seconds
      const interval = setInterval(loadNotificationStats, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleNotificationCountChange = (newCount) => {
    setNotificationCount(newCount);
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setSidebarVisible(!sidebarVisible);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const closeMobileSidebar = () => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  };

  const navigationItems = [
    // User section
    {
      path: '/',
      label: 'Dashboard',
      icon: 'fas fa-tachometer-alt',
      roles: ['admin', 'manager', 'user'],
      section: 'user'
    },
    {
      path: '/domains',
      label: 'Domains',
      icon: 'fas fa-globe',
      roles: ['admin', 'manager', 'user'],
      section: 'user'
    },
    {
      path: '/released',
      label: 'Released',
      icon: 'fas fa-download',
      roles: ['admin', 'manager', 'user'],
      section: 'user'
    },
    {
      path: '/orders',
      label: 'Orders',
      icon: 'fas fa-shopping-cart',
      roles: ['admin', 'manager', 'user'],
      section: 'user'
    },
    {
      path: '/payments',
      label: 'Payments',
      icon: 'fas fa-credit-card',
      roles: ['admin', 'manager', 'user'],
      section: 'user'
    },
    {
      path: '/invoices',
      label: 'Invoices',
      icon: 'fas fa-file-invoice',
      roles: ['admin', 'manager', 'user'],
      section: 'user'
    },

    // Manager section
    {
      path: '/users',
      label: 'Users',
      icon: 'fas fa-users',
      roles: ['admin', 'manager'],
      section: 'manager'
    },
    {
      path: '/epp-accounts',
      label: 'EPP Accounts',
      icon: 'fas fa-server',
      roles: ['admin', 'manager'],
      section: 'manager'
    },
    {
      path: '/epp-logs',
      label: 'EPP Logs',
      icon: 'fas fa-list-alt',
      roles: ['admin', 'manager'],
      section: 'manager'
    },
    {
      path: '/epp-tools',
      label: 'EPP Tools',
      icon: 'fas fa-tools',
      roles: ['admin', 'manager'],
      section: 'manager'
    },
    {
      path: '/audit-logs',
      label: 'Audit Logs',
      icon: 'fas fa-history',
      roles: ['admin'],
      section: 'manager'
    },

    // Admin section
    {
      path: '/epp-categories',
      label: 'EPP Categories',
      icon: 'fas fa-sitemap',
      roles: ['admin'],
      section: 'admin'
    },
    {
      path: '/sources',
      label: 'Sources',
      icon: 'fas fa-database',
      roles: ['admin'],
      section: 'admin'
    },
    {
      path: '/data-sources',
      label: 'Data Sources',
      icon: 'fas fa-chart-line',
      roles: ['admin'],
      section: 'admin'
    },
    {
      path: '/schedulers',
      label: 'Schedulers',
      icon: 'fas fa-clock',
      roles: ['admin'],
      section: 'admin'
    },
    {
      path: '/news',
      label: 'News',
      icon: 'fas fa-newspaper',
      roles: ['admin'],
      section: 'admin'
    },
  ];

  const filteredNavItems = navigationItems.filter(item =>
    hasRole(item.roles)
  );

  // Group navigation items by section
  const groupedNavItems = filteredNavItems.reduce((acc, item) => {
    const section = item.section;
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(item);
    return acc;
  }, {});

  const getSectionTitle = (section) => {
    switch (section) {
      case 'user': return 'General';
      case 'manager': return 'Management';
      case 'admin': return 'Administration';
      default: return section;
    }
  };

  return (
    <div className="app-container">
      <Navbar bg="dark" variant="dark" expand="lg" className="navbar-custom">
        <Container fluid>
          {/* LEFT SIDE */}
          <div className="d-flex align-items-center">
            <button
              className="btn btn-link text-light p-0 me-3"
              onClick={toggleSidebar}
            >
              <i className="fas fa-bars"></i>
            </button>

            <Navbar.Brand as={Link} to="/">
              <i className="fas fa-gem"></i>
              <span>Expired Treasure</span>
            </Navbar.Brand>
          </div>

          {/* RIGHT SIDE */}
          <div className="d-flex align-items-center gap-3 ms-auto">
            <NotificationsDropdown
              notificationCount={notificationCount}
              onNotificationCountChange={handleNotificationCountChange}
            />

            <Dropdown align="end">
              <Dropdown.Toggle variant="link" className="nav-link text-light dropdown-toggle d-flex align-items-center">
                <i className="fas fa-user-circle me-2"></i>
                <span>{user?.username}</span>
              </Dropdown.Toggle>

              <Dropdown.Menu className="dropdown-menu-dark">
                <Dropdown.Item as={Link} to="/profile">
                  <i className="fas fa-user" style={{paddingRight: '10px'}}></i>
                  Profile
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/settings">
                  <i className="fas fa-cog" style={{paddingRight: '10px'}}></i>
                  Settings
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={logout}>
                  <i className="fas fa-sign-out-alt" style={{paddingRight: '10px'}}></i>
                  Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Container>
      </Navbar>

      <div className="d-flex flex-grow-1">
        <nav className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${isMobile && sidebarVisible ? 'show' : ''}`}>
          <div className="nav flex-column">
            {['user', 'manager', 'admin'].map((section, sectionIndex) => {
              const sectionItems = groupedNavItems[section];
              if (!sectionItems || sectionItems.length === 0) return null;

              return (
                <div key={section} className="nav-section">
                  {/* Section separator and title */}
                  {sectionIndex > 0 && <div className="nav-divider"></div>}
                  {!sidebarCollapsed && (
                    <div className="nav-section-title">
                      {getSectionTitle(section)}
                    </div>
                  )}

                  {/* Section items */}
                  {sectionItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                      onClick={closeMobileSidebar}
                      title={sidebarCollapsed ? item.label : ''}
                    >
                      <i className={item.icon}></i>
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              );
            })}
          </div>
        </nav>

        {/* Mobile overlay */}
        {isMobile && sidebarVisible && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1049,
              marginTop: '57px'
            }}
            onClick={closeMobileSidebar}
          />
        )}

        <main className="flex-grow-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;